import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraCheckbox, SpecraInput, SpecraOverlayDrawer, SpecraSelect, SpecraTextarea, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { requirementSource } from '../../../hooks/useComplianceCoverage';
import { useProjectFiles } from '../../../hooks/useProjectFiles';
import { useProjectVolumes } from '../../../hooks/useProjectVolumes';
import { projectDataClient } from '../../../lib/connect/clients';
import { DocumentViewerDrawer } from '../DocumentViewer/DocumentViewerDrawer';
import { flattenSections } from '../v2/volume/workingOutlineHooks';
export function CoverageInspector({ projectId, item, currentVersionId, confirmations, members, canEdit, mapping, confirmation, onClose }) {
    const volumes = useProjectVolumes(projectId);
    const outlines = useQueries({
        queries: (volumes.data ?? []).map((volume)=>({
                queryKey: [
                    'outline-sections',
                    projectId,
                    volume.volumeId
                ],
                queryFn: async ()=>(await projectDataClient.getSectionMap({
                        projectId,
                        volumeId: volume.volumeId
                    })).sections
            }))
    });
    const files = useProjectFiles(projectId);
    const workbookVolumes = (volumes.data ?? []).filter((volume)=>volume.kind === 'workbook');
    const sheets = useQuery({
        queryKey: [
            'coverageWorkbookLocations',
            projectId,
            workbookVolumes.map((volume)=>volume.volumeId)
        ],
        queryFn: async ()=>(await Promise.all(workbookVolumes.map(async (volume)=>(await projectDataClient.listWorkbookSheets({
                    projectId,
                    volumeId: volume.volumeId
                })).sheets.map((sheet)=>({
                        ...sheet,
                        volumeId: volume.volumeId
                    }))))).flat()
    });
    const sections = outlines.flatMap((outline, index)=>flattenSections(outline.data ?? []).map((section)=>({
                ...section,
                volumeId: volumes.data?.[index]?.volumeId ?? ''
            })));
    const [locations, setLocations] = useState(item.locations.map((location)=>({
            ...location,
            accountableUserIds: [
                ...location.accountableUserIds
            ]
        })));
    const [dirty, setDirty] = useState(false);
    const [attachment, setAttachment] = useState(null);
    const [state, setState] = useState(item.substantiveState);
    const [evidence, setEvidence] = useState('');
    const [error, setError] = useState('');
    const [saved, setSaved] = useState('');
    const [saving, setSaving] = useState(false);
    const source = requirementSource(item);
    const historical = item.superseded || item.versionId !== currentVersionId;
    const disabled = !canEdit || historical || saving;
    const patch = (index, update)=>{
        setDirty(true);
        setLocations((previous)=>previous.map((location, i)=>i === index ? Object.assign({
                    ...location,
                    accountableUserIds: [
                        ...location.accountableUserIds
                    ]
                }, update) : location));
    };
    const person = (id)=>members.find((member)=>member.userId === id)?.name || members.find((member)=>member.userId === id)?.email || id;
    const choices = [
        ...(volumes.data ?? []).map((volume)=>({
                kind: 'volume',
                id: volume.volumeId,
                label: volume.displayName
            })),
        ...sections.map((section)=>({
                kind: 'section',
                id: section.sectionId,
                label: section.title
            })),
        ...(sheets.data ?? []).map((sheet)=>({
                kind: 'workbook',
                id: sheet.id,
                label: sheet.sheetName
            })),
        ...(files.data?.files ?? []).map((file)=>({
                kind: 'attachment',
                id: file.attachmentId,
                label: file.name
            }))
    ];
    const responseURL = (location)=>{
        const base = `/proposal/${projectId}/v2`;
        if (location.kind === 'volume') return `${base}/volume/${encodeURIComponent(location.id)}`;
        if (location.kind === 'section') {
            const section = sections.find((candidate)=>candidate.sectionId === location.id);
            return section ? `${base}/volume/${encodeURIComponent(section.volumeId)}/section/${encodeURIComponent(section.sectionId)}` : undefined;
        }
        if (location.kind === 'workbook') {
            const sheet = sheets.data?.find((candidate)=>candidate.id === location.id);
            return sheet ? `${base}/volume/${encodeURIComponent(sheet.volumeId)}?workbookSheet=${encodeURIComponent(sheet.sheetName)}` : undefined;
        }
        return undefined;
    };
    const save = async (confirm)=>{
        if (disabled || confirm && (dirty || !evidence.trim())) return;
        setSaving(true);
        setError('');
        setSaved('');
        try {
            if (confirm) await confirmation.mutateAsync({
                item,
                substantiveState: state,
                evidence: evidence.trim()
            });
            else await mapping.mutateAsync({
                item,
                locations
            });
            setSaved(confirm ? 'Coverage confirmed' : 'Mappings saved; substantive coverage reset to UNADDRESSED');
        } catch  {
            setError('Unable to save. The source or revision may have changed. Close and reload coverage before reviewing and trying again.');
        } finally{
            setSaving(false);
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
                open: true,
                modal: !attachment,
                contentProps: {
                    onInteractOutside: (event)=>{
                        if (attachment) event.preventDefault();
                    }
                },
                onOpenChange: (open)=>{
                    if (!open) onClose();
                },
                title: `Requirement ${item.requirementId}`,
                testId: Testids.complianceCoverage.inspector,
                width: "lg",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            children: source.Text || source.SourceExactText
                        }),
                        /*#__PURE__*/ _jsxs("p", {
                            children: [
                                "Source version: ",
                                item.versionId,
                                " · § ",
                                source.SourceSectionNumber || 'unspecified',
                                " · page ",
                                source.SourcePageNumber || 'unspecified'
                            ]
                        }),
                        historical && /*#__PURE__*/ _jsx("p", {
                            children: "Superseded history — read only. Confirmation does not carry to the current source."
                        }),
                        /*#__PURE__*/ _jsxs("p", {
                            children: [
                                "Substantive coverage: ",
                                item.substantiveState.toUpperCase()
                            ]
                        }),
                        item.confirmedBy && /*#__PURE__*/ _jsxs("p", {
                            children: [
                                "Confirmed by ",
                                person(item.confirmedBy),
                                " ·",
                                ' ',
                                item.confirmedAt?.toDate().toLocaleString(),
                                " · ",
                                item.evidence
                            ]
                        }),
                        /*#__PURE__*/ _jsx("h3", {
                            className: "font-semibold",
                            children: "Response locations and accountable people"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            children: "Saving mappings clears confirmation. Draft completion is independent of substantive coverage."
                        }),
                        (volumes.isLoading || outlines.some((outline)=>outline.isLoading) || files.isLoading || sheets.isLoading) && /*#__PURE__*/ _jsx("output", {
                            children: "Loading response locations…"
                        }),
                        (volumes.isError || outlines.some((outline)=>outline.isError) || files.isError || sheets.isError) && /*#__PURE__*/ _jsx("p", {
                            role: "alert",
                            children: "Some response locations could not load. Close and reload to try again."
                        }),
                        /*#__PURE__*/ _jsxs("fieldset", {
                            disabled: disabled || !!error,
                            className: "space-y-3",
                            children: [
                                locations.map((location, index)=>/*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-2 rounded border border-border p-3",
                                        "data-testid": Testids.complianceCoverage.location,
                                        children: [
                                            /*#__PURE__*/ _jsxs(SpecraSelect, {
                                                "aria-label": `Response location ${index + 1}`,
                                                value: `${location.kind}:${location.id}`,
                                                onChange: (event)=>{
                                                    const choice = choices.find((candidate)=>`${candidate.kind}:${candidate.id}` === event.target.value);
                                                    if (choice) patch(index, {
                                                        kind: choice.kind,
                                                        id: choice.id,
                                                        locator: choice.label
                                                    });
                                                },
                                                children: [
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "",
                                                        children: "Choose a response location"
                                                    }),
                                                    !choices.some((choice)=>choice.kind === location.kind && choice.id === location.id) && location.id && /*#__PURE__*/ _jsxs("option", {
                                                        value: `${location.kind}:${location.id}`,
                                                        children: [
                                                            location.locator || location.id,
                                                            " (unavailable)"
                                                        ]
                                                    }),
                                                    choices.map((choice)=>/*#__PURE__*/ _jsxs("option", {
                                                            value: `${choice.kind}:${choice.id}`,
                                                            children: [
                                                                choice.kind,
                                                                ": ",
                                                                choice.label
                                                            ]
                                                        }, `${choice.kind}:${choice.id}`))
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                "aria-label": `Locator ${index + 1}`,
                                                value: location.locator,
                                                onChange: (event)=>patch(index, {
                                                        locator: event.target.value
                                                    }),
                                                placeholder: "Section, cell range or page reference"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraSelect, {
                                                multiple: true,
                                                "aria-label": `Accountable people ${index + 1}`,
                                                value: location.accountableUserIds,
                                                onChange: (event)=>patch(index, {
                                                        accountableUserIds: Array.from(event.target.selectedOptions, (option)=>option.value)
                                                    }),
                                                children: members.map((member)=>/*#__PURE__*/ _jsx("option", {
                                                        value: member.userId,
                                                        children: member.name || member.email
                                                    }, member.userId))
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-2",
                                                children: [
                                                    /*#__PURE__*/ _jsx(SpecraCheckbox, {
                                                        "aria-label": `Draft complete ${index + 1}`,
                                                        checked: location.draftComplete,
                                                        onCheckedChange: (checked)=>patch(index, {
                                                                draftComplete: checked === true
                                                            })
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        children: "Draft complete — does not confirm coverage"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                                "aria-label": `Location evidence ${index + 1}`,
                                                value: location.evidence,
                                                onChange: (event)=>patch(index, {
                                                        evidence: event.target.value
                                                    })
                                            }),
                                            location.kind === 'attachment' && /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "link",
                                                onClick: ()=>setAttachment({
                                                        documentId: location.id,
                                                        filename: files.data?.files.find((file)=>file.attachmentId === location.id)?.name || location.locator || 'Response attachment'
                                                    }),
                                                children: "Open response"
                                            }),
                                            responseURL(location) && /*#__PURE__*/ _jsx(Link, {
                                                className: specraButtonVariants({
                                                    variant: 'link'
                                                }),
                                                to: responseURL(location) ?? '',
                                                children: "Open response"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                onClick: ()=>{
                                                    setDirty(true);
                                                    setLocations((previous)=>previous.filter((_, i)=>i !== index));
                                                },
                                                children: "Remove location"
                                            })
                                        ]
                                    }, `${index}:${location.kind}:${location.id}`)),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    onClick: ()=>{
                                        setDirty(true);
                                        setLocations((previous)=>[
                                                ...previous,
                                                {
                                                    kind: '',
                                                    id: '',
                                                    locator: '',
                                                    accountableUserIds: [],
                                                    draftComplete: false,
                                                    evidence: ''
                                                }
                                            ]);
                                    },
                                    children: "Add response location"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": Testids.complianceCoverage.saveMappings,
                                    disabled: !dirty || locations.some((location)=>!location.id),
                                    onClick: ()=>save(false),
                                    children: "Save mappings"
                                }),
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "font-semibold",
                                    children: "Explicit human confirmation"
                                }),
                                dirty && /*#__PURE__*/ _jsx("p", {
                                    children: "Save mappings before confirming coverage."
                                }),
                                /*#__PURE__*/ _jsxs(SpecraSelect, {
                                    "data-testid": Testids.proposalGuides.coverageState,
                                    "aria-label": "Substantive state",
                                    value: state,
                                    onChange: (event)=>setState(event.target.value),
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "unaddressed",
                                            children: "Unaddressed"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "partial",
                                            children: "Partial"
                                        }),
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "addressed",
                                            children: "Addressed"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraTextarea, {
                                    "data-testid": Testids.proposalGuides.coverageEvidence,
                                    "aria-label": "Confirmation evidence",
                                    value: evidence,
                                    onChange: (event)=>setEvidence(event.target.value),
                                    placeholder: "Explain the substantive review and supporting evidence"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    "data-testid": Testids.complianceCoverage.confirm,
                                    disabled: dirty || !evidence.trim(),
                                    onClick: ()=>save(true),
                                    children: "Confirm substantive coverage"
                                })
                            ]
                        }),
                        saving && /*#__PURE__*/ _jsx("output", {
                            children: "Saving coverage…"
                        }),
                        saved && /*#__PURE__*/ _jsx("output", {
                            "data-testid": Testids.proposalGuides.coverageSaved,
                            children: saved
                        }),
                        error && /*#__PURE__*/ _jsx("p", {
                            role: "alert",
                            children: error
                        }),
                        /*#__PURE__*/ _jsxs("details", {
                            children: [
                                /*#__PURE__*/ _jsx("summary", {
                                    children: "Confirmation history"
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    children: confirmations.filter((entry)=>entry.versionId === item.versionId && entry.requirementId === item.requirementId).map((entry)=>/*#__PURE__*/ _jsxs("li", {
                                            children: [
                                                entry.substantiveState.toUpperCase(),
                                                " · ",
                                                person(entry.actor),
                                                " ·",
                                                ' ',
                                                entry.confirmedAt?.toDate().toLocaleString(),
                                                " · ",
                                                entry.evidence
                                            ]
                                        }, entry.id))
                                })
                            ]
                        })
                    ]
                })
            }),
            attachment && /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                isOpen: true,
                onClose: ()=>setAttachment(null),
                projectId: projectId,
                document: attachment,
                mode: "extension"
            })
        ]
    });
}
